import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { learnArticlePath } from '@/features/aprender/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const ARTICLE_ID = 'luz-indexada-o-fija-restaurante'
const k = (f: string) => `news.articles.${ARTICLE_ID}.${f}`

const REF = {
    omiePeriodico: {
        href: 'https://elperiodicodelaenergia.com/la-luz-en-el-mercado-mayorista-marcara-este-martes-los-201-99-euros-mwh-su-maximo-anual/',
        domain: 'elperiodicodelaenergia.com',
        label: 'OMIE · El Periódico de la Energía',
    },
    efeInfobae: {
        href: 'https://www.infobae.com/espana/agencias/2026/09/29/el-gas-empuja-el-precio-mayorista-de-la-electricidad-en-septiembre-hasta-su-maximo-anual/',
        domain: 'infobae.com',
        label: 'EFE · Infobae',
    },
    energyNews: {
        href: 'https://www.energynews.es/precio-mayorista-electricidad-agosto-2026/',
        domain: 'energynews.es',
        label: 'EnergyNews',
    },
    futurosPeriodico: {
        href: 'https://elperiodicodelaenergia.com/los-futuros-de-electricidad-y-gas-anticipan-un-invierno-con-precios-altos-en-europa/',
        domain: 'elperiodicodelaenergia.com',
        label: 'Futuros · El Periódico de la Energía',
    },
    eneon: {
        href: 'https://eneon.es/cae-mercado-energetico-otono-invierno-2026-2027/',
        domain: 'eneon.es',
        label: 'n)ON · OMIP',
    },
    aeat: {
        href: 'https://sede.agenciatributaria.gob.es/Sede/todas-noticias/2026/septiembre/30/nuevas-medidas-marco-plan-integral-medio.html',
        domain: 'agenciatributaria.gob.es',
        label: 'Agencia Tributaria',
    },
    marinaPlaza: {
        href: 'https://lamarina.eldiario.es/2026/09/06/la-hosteleria-de-la-marina-alta-alerta-de-un-notable-descenso-de-la-rentabilidad-de-bares-y-restaurantes-en-los-ultimos-anos/',
        domain: 'lamarina.eldiario.es',
        label: 'La Marina Plaza · AEHTMA',
    },
    omie: {
        href: 'https://www.omie.es/',
        domain: 'omie.es',
        label: 'OMIE',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

export function LuzIndexadaOFijaArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'precio-201', label: t(k('toc.precio'), '201,99 euros') },
        { id: 'como-se-si-tengo-la-luz-indexada', label: t(k('toc.indexada'), '¿Cómo sé si tengo la luz indexada?') },
        { id: 'fijo-ya-no-es-barato', label: t(k('toc.fijo'), 'El fijo que te ofrezcan ahora ya no es barato') },
        { id: 'si-das-cenas', label: t(k('toc.cenas'), 'Si das cenas, pagas más que la media') },
        { id: 'cuanto-en-euros', label: t(k('toc.euros'), 'Cuánto es esto en euros') },
        { id: 'me-conviene-pasarme-a-fijo', label: t(k('toc.conviene'), '¿Me conviene pasarme a fijo?') },
        { id: 'tres-cosas', label: t(k('toc.tres'), 'Antes de hablar con nadie, busca estas tres cosas') },
        { id: 'lo-que-nadie-sabe', label: t(k('toc.nadie'), 'Lo que nadie sabe todavía') },
    ]

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo de luz indexada o fija y quiero que reviséis mis tres últimas facturas con el bróker energético.',
        ),
    )

    const checklist: ReactNode[] = [
        <>
            <strong>{t(k('checklist.1.strong'), 'Tu contrato:')}</strong>{' '}
            {t(k('checklist.1.after'), 'si es indexado o fijo, cuándo acaba y si tiene permanencia.')}
        </>,
        <>
            <strong>{t(k('checklist.2.strong'), 'Tus tres últimas facturas:')}</strong>{' '}
            {t(k('checklist.2.after'), 'cuántos kWh gastaste y en qué franjas horarias.')}
        </>,
        <>
            <strong>{t(k('checklist.3.strong'), 'Lo que pagaste de media por kWh')}</strong>{' '}
            {t(
                k('checklist.3.after'),
                'en esas tres facturas. Con ese número se compara cualquier oferta, no con el precio que sale en las noticias.',
            )}
        </>,
    ]

    const sources = [
        {
            label: t(
                k('sources.1'),
                'OMIE, precio del mercado diario del 6 de octubre de 2026, recogido por EFE en El Periódico de la Energía',
            ),
            href: REF.omiePeriodico.href,
        },
        {
            label: t(
                k('sources.2'),
                'EFE, precio medio de septiembre de 2026 y de septiembre de 2025, 29 de septiembre de 2026',
            ),
            href: REF.efeInfobae.href,
        },
        {
            label: t(k('sources.3'), 'EnergyNews, precio de agosto de 2026 y precio en horas de sol'),
            href: REF.energyNews.href,
        },
        {
            label: t(
                k('sources.4'),
                'El Periódico de la Energía, precio de la luz comprada por adelantado para el invierno',
            ),
            href: REF.futurosPeriodico.href,
        },
        {
            label: t(
                k('sources.5'),
                'Precio de la luz de octubre a diciembre comprada por adelantado a mediados de septiembre, mercado OMIP: n)ON, panorama energético otoño-invierno 2026-2027',
            ),
            href: REF.eneon.href,
        },
        {
            label: t(
                k('sources.6'),
                'Agencia Tributaria, medidas del Real Decreto-ley 25/2026, 30 de septiembre de 2026',
            ),
            href: REF.aeat.href,
        },
        {
            label: t(
                k('sources.7'),
                'La Marina Plaza, declaraciones de AEHTMA sobre la rentabilidad de bares y restaurantes, 6 de septiembre de 2026',
            ),
            href: REF.marinaPlaza.href,
        },
    ]

    const ownSource = t(k('sources.8'), 'Ejemplo de consumo y cálculos: elaboración propia de Tipi')

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(k('dek.before'), 'El 6 de octubre de 2026, la luz en el mercado mayorista marcó ')}
                            <strong>{t(k('dek.price'), '201,99 euros el megavatio hora')}</strong>
                            {t(
                                k('dek.afterPrice'),
                                ', el precio más alto del año. En septiembre la media fue de 143,72 euros, más del doble que hace un año. Si tienes la luz indexada, esa subida te llega en la próxima factura. Pasarte a precio fijo te protege, pero el fijo que te ofrezcan ahora ya viene caro. La pregunta es si te compensa pagar más por dormir tranquilo este invierno.',
                            )}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Factura de la luz sobre la encimera de una cocina de restaurante, con campana y luces de servicio',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <ArticleReadingLayout
                    intro={
                        <div className={article.prose}>
                            <h2 id='precio-201' className={cn(article.h2, 'mt-0')}>
                                {t(k('h2.precio'), '201,99 euros')}
                            </h2>
                            <p className={article.p}>
                                {t(
                                    k('precio.p1.before'),
                                    'Ese es el precio al que se compró la luz al por mayor el martes 6 de octubre, según ',
                                )}
                                <span className={article.cite}>
                                    <a
                                        href={REF.omiePeriodico.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('precio.p1.link'), 'los datos de OMIE')}
                                    </a>
                                    {t(
                                        k('precio.p1.after'),
                                        ', el operador que fija ese precio cada día. Es el más alto de 2026.',
                                    )}
                                    <SourceChip source='omiePeriodico' />
                                </span>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(k('precio.p2'), 'Y no es un mal día suelto:')}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <div className={article.tableWrap}>
                        <table className={article.tableFacts}>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.col.month'), 'Mes')}</th>
                                    <th scope='col'>{t(k('table.col.price'), 'Precio medio al por mayor')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th scope='row'>{t(k('table.sep25.label'), 'Septiembre de 2025')}</th>
                                    <td>{t(k('table.sep25.value'), '61 €')}</td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.aug26.label'), 'Agosto de 2026')}</th>
                                    <td>{t(k('table.aug26.value'), '118 €')}</td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.sep26.label'), 'Septiembre de 2026')}</th>
                                    <td>
                                        <strong>{t(k('table.sep26.value'), '144 €')}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <span className={article.cite}>
                                {t(
                                    k('precio.caption'),
                                    'Fuente: OMIE, con las medias recogidas por EFE y EnergyNews. Euros por megavatio hora.',
                                )}
                                <SourceChip source='efeInfobae' />
                                <SourceChip source='energyNews' />
                            </span>
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('precio.cause'),
                                'La causa es el gas, que se ha encarecido con el conflicto de Oriente Medio.',
                            )}
                        </p>
                    </div>

                    <section id='como-se-si-tengo-la-luz-indexada'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.indexada'), '¿Cómo sé si tengo la luz indexada?')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>{t(k('indexada.intro'), 'Hay dos formas de pagar la energía:')}</p>
                            <ul className={article.dashList}>
                                <li className={article.dashItem}>
                                    <strong>{t(k('indexada.indexed.strong'), 'Indexada:')}</strong>{' '}
                                    {t(
                                        k('indexada.indexed.after'),
                                        'pagas lo que marque el mercado cada hora, más el margen de la compañía. Si el mercado sube, tu factura sube.',
                                    )}
                                </li>
                                <li className={article.dashItem}>
                                    <strong>{t(k('indexada.fixed.strong'), 'Fija:')}</strong>{' '}
                                    {t(
                                        k('indexada.fixed.after'),
                                        'pagas el mismo precio por kWh durante todo el contrato, normalmente un año.',
                                    )}
                                </li>
                            </ul>
                            <p className={article.p}>
                                {t(k('indexada.p2.before'), 'Coge tu última factura. ')}
                                <strong>
                                    {t(
                                        k('indexada.p2.strong'),
                                        'Si el precio del kWh cambia de un mes a otro, o pone "indexado" u "OMIE", tienes la luz indexada.',
                                    )}
                                </strong>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('indexada.p3'),
                                    'Si tu cocina tiene cámaras, horno y campana, casi seguro que tienes más de 15 kW contratados. Eso quiere decir que no estás en la tarifa regulada del Gobierno, que solo existe hasta 10 kW: estás en mercado libre, con lo que hayas firmado.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='fijo-ya-no-es-barato'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.fijo'), 'El fijo que te ofrezcan ahora ya no es barato')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('fijo.p1.before'), 'Primero la pega. ')}
                                <strong>
                                    {t(
                                        k('fijo.p1.strong'),
                                        'Nadie te va a vender este otoño un fijo a precio de agosto.',
                                    )}
                                </strong>
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('fijo.p2'),
                                    'Las compañías ponen el precio fijo según lo que el mercado cree que va a costar la luz en los próximos meses. Y ahora mismo cree que el invierno va a ser caro: la luz de octubre a diciembre ',
                                )}
                                <span className={article.cite}>
                                    <a
                                        href={REF.futurosPeriodico.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(
                                            k('fijo.p2.link'),
                                            'se estaba comprando por adelantado a 132 euros a primeros de septiembre',
                                        )}
                                    </a>
                                    <SourceChip source='futurosPeriodico' />
                                </span>
                                {t(k('fijo.p2.mid'), ', y a mitad de mes ya rondaba los 145')}
                                <span className={article.cite}>
                                    <SourceChip source='eneon' />
                                </span>
                                {t(k('fijo.p2.after'), '.')}
                            </p>
                            <p className={article.p}>
                                {t(k('fijo.p3.before'), 'Así que un fijo no es un descuento. ')}
                                <strong>
                                    {t(k('fijo.p3.strong'), 'Es un seguro: pagas un precio alto, pero sabes cuál es.')}
                                </strong>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('fijo.p4'),
                                    'Y tiene una segunda pega. Si firmas un año, pagas ese precio también en primavera, cuando el mercado espera que la luz baje. Parte del susto de diciembre lo pagarías en mayo.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='si-das-cenas'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.cenas'), 'Si das cenas, pagas más que la media')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('cenas.p1'), 'El precio medio del día no es el que pagas tú.')}
                            </p>
                            <p className={article.p}>
                                {t(k('cenas.p2.before'), 'En agosto, la luz salió a ')}
                                <span className={article.cite}>
                                    <a
                                        href={REF.energyNews.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(
                                            k('cenas.p2.link'),
                                            '26 euros en las horas de sol y a 164 euros el resto del día',
                                        )}
                                    </a>
                                    {t(
                                        k('cenas.p2.after'),
                                        '. El 6 de octubre, la hora más cara fue hacia las nueve de la noche.',
                                    )}
                                    <SourceChip source='energyNews' />
                                </span>
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('cenas.p3.before'),
                                    'Un bar de desayunos y comidas gasta la luz cuando es más barata. ',
                                )}
                                <strong>
                                    {t(
                                        k('cenas.p3.strong'),
                                        'Un restaurante de cenas enciende horno y campana justo en las horas más caras.',
                                    )}
                                </strong>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('cenas.p4'),
                                    'Con la tarifa indexada, el que da cenas paga más que la media que sale en las noticias.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='cuanto-en-euros'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.euros'), 'Cuánto es esto en euros')}
                        </h2>
                        <div className={article.prose}>
                            <p className={cn(article.p, 'text-article-muted')}>
                                <em>
                                    {t(
                                        k('euros.note'),
                                        'Ejemplo nuestro, con números redondos: un restaurante con cocina completa que gasta 4.000 kWh de luz al mes. Solo contamos la subida del precio de la energía; el resto de la factura va aparte.',
                                    )}
                                </em>
                            </p>
                        </div>
                        <div className={article.tableWrap}>
                            <table className={article.tableFacts}>
                                <tbody>
                                    <tr>
                                        <th scope='row'>
                                            {t(k('euros.table.month.label'), 'Más al mes que hace un año')}
                                        </th>
                                        <td>
                                            <strong>{t(k('euros.table.month.value'), 'unos 330 €')}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>
                                            {t(
                                                k('euros.table.winter.label'),
                                                'Más de octubre a diciembre, si el mercado se queda donde espera',
                                            )}
                                        </th>
                                        <td>
                                            <strong>{t(k('euros.table.winter.value'), 'entre 850 y 1.000 €')}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(
                                        k('euros.p1'),
                                        'Hoy un bar o restaurante gana entre un 3 % y un 4 % de lo que factura, según las asociaciones del sector. Con 300.000 euros de venta al año, eso son entre 9.000 y 12.000 euros de beneficio.',
                                    )}
                                    <SourceChip source='marinaPlaza' />
                                </span>
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('euros.p2'),
                                        'Esos tres meses de luz se llevan cerca de una décima parte del beneficio del año.',
                                    )}
                                </strong>{' '}
                                {t(k('euros.p2.after'), 'Sin vender un plato menos.')}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(k('euros.p3.before'), 'Es dinero que no ves en la caja del día y sí en ')}
                                <LocaleLink
                                    to={learnArticlePath('beneficio-real-restaurante-cuanto-puedo-sacar')}
                                    className={article.link}
                                >
                                    {t(k('euros.p3.link'), 'lo que te queda de verdad a final de año')}
                                </LocaleLink>
                                {t(k('euros.p3.after'), '.')}
                            </p>
                        </div>
                    </section>

                    <section id='me-conviene-pasarme-a-fijo'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.conviene'), '¿Me conviene pasarme a fijo?')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>{t(k('conviene.switch'), 'Mira un fijo si:')}</p>
                            <ul className={article.dashList}>
                                <li className={article.dashItem}>
                                    {t(
                                        k('conviene.switch.1'),
                                        'Tienes la luz indexada y tu contrato no tiene permanencia, o le queda poco.',
                                    )}
                                </li>
                                <li className={article.dashItem}>
                                    {t(k('conviene.switch.2'), 'Tu negocio fuerte es por la tarde y por la noche.')}
                                </li>
                                <li className={article.dashItem}>
                                    {t(
                                        k('conviene.switch.3'),
                                        'Trescientos euros de más al mes te descuadran y prefieres saber lo que vas a pagar.',
                                    )}
                                </li>
                            </ul>
                            <p className={article.p}>{t(k('conviene.stay'), 'Quédate como estás si:')}</p>
                            <ul className={article.dashList}>
                                <li className={article.dashItem}>
                                    {t(
                                        k('conviene.stay.1'),
                                        'Tu contrato tiene permanencia con penalización. Pon esa cifra al lado del ahorro antes de decidir.',
                                    )}
                                </li>
                                <li className={article.dashItem}>
                                    {t(k('conviene.stay.2'), 'Trabajas sobre todo de día.')}
                                </li>
                                <li className={article.dashItem}>
                                    {t(
                                        k('conviene.stay.3'),
                                        'Puedes aguantar un invierno caro a cambio de pagar menos en primavera, si el mercado acierta.',
                                    )}
                                </li>
                            </ul>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('conviene.p3'),
                                    'Y si firmas un fijo, que sea fijo de verdad: que no tenga cláusulas para subirte el precio a mitad de contrato.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='tres-cosas'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.tres'), 'Antes de hablar con nadie, busca estas tres cosas')}
                        </h2>
                        <div className={article.prose}>
                            <NewsArticleNumList items={checklist} />
                        </div>
                    </section>

                    <section id='lo-que-nadie-sabe'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.nadie'), 'Lo que nadie sabe todavía')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>{t(k('nadie.p1.strong'), 'Nadie sabe cuánto va a durar.')}</strong>{' '}
                                {t(
                                    k('nadie.p1.after'),
                                    'Lo que el mercado espera hoy para el invierno ha subido de 91 a 145 euros entre mayo y septiembre, y depende del gas. Por eso no te damos un precio fijo de referencia: solo vale comparar con una oferta real, con fecha.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(k('nadie.p2.strong'), 'La bajada de impuestos de noviembre casi no te toca.')}
                                </strong>{' '}
                                {t(k('nadie.p2.before'), 'El ')}
                                <span className={article.cite}>
                                    <a
                                        href={REF.aeat.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('nadie.p2.link'), 'Real Decreto-ley 25/2026')}
                                    </a>
                                    <SourceChip source='aeat' />
                                </span>
                                {t(
                                    k('nadie.p2.after'),
                                    ' baja el IVA de la luz al 10 % solo para potencias de hasta 10 kW. La rebaja del impuesto eléctrico sí podría llegarte, pero depende de un dato de precios del INE que aún no ha salido.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>{t(k('nadie.p3.strong'), 'Nuestro ejemplo es solo eso.')}</strong>{' '}
                                {t(
                                    k('nadie.p3.after'),
                                    'Un bar sin cocina puede gastar la mitad, y un asador con cámaras, el doble.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('nadie.p4'),
                                    'Si tienes la luz indexada y no sabes si te compensa cambiar, mándanos tus tres últimas facturas y las revisamos con un bróker energético profesional con el que trabajamos. Si lo que te conviene es quedarte como estás, te lo decimos igual.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>{t(k('nadie.p5.strong'), 'Si lo haces con Tipi, hay doble retorno.')}</strong>{' '}
                                {t(
                                    k('nadie.p5.after'),
                                    'Si cambias de contrato a través de ese bróker, ahorras mes a mes en la factura —si la oferta lo justifica— y la comisión que paga la compañía eléctrica te llega a ti como saldo para usar Tipi gratis. Esa comisión no se la queda Tipi. Nosotros no vendemos luz, y la decisión es tuya.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('nadie.p6'),
                                    'La factura de noviembre ya trae este octubre dentro. Mejor mirar la de ahora antes.',
                                )}
                            </p>
                        </div>
                    </section>
                </ArticleReadingLayout>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), 'Doble retorno: menos luz al mes y Tipi gratis')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Mándanos tus tres últimas facturas. Si te conviene cambiar, ahorras en la factura y la comisión de la compañía te llega como saldo para usar Tipi. Si te conviene quedarte, te lo decimos igual.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                        <li>
                            <a href={REF.omie.href} target='_blank' rel='noopener noreferrer' className={article.link}>
                                {t(k('sources.omie'), 'OMIE, operador del mercado diario')}
                            </a>
                        </li>
                        <li>{ownSource}</li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
